import React, { useEffect } from "react";
import _ from "lodash";
import cx from "classnames";

import Button from "../../../../components/button";

import Actions from "../../../../components/actionbar";
import { useTransition, animated } from "@react-spring/web";
import { useSelector } from "react-redux";
import Toolbar from "../../../../components/toolbar";
import Slider from "../../../../components/slider";
import { Collection } from "../../../../types/collections";
import { CollectionCard } from "../card";

import styles from "./edit.module.scss";
import { useDispatch } from "react-redux";
import {
  createCollection,
  deleteCollection,
  invalidateCollectionCreationState,
} from "../../../../redux/actions/collections";
import { CollectionsContext } from "../..";
import { Input } from "../../../../components/input";
import { useNavigate } from "react-router-dom";
import Reveal from "../../../../components/animation";
import userRoleService from "../../../../services/userRoles.service";
import { CreateCollectionDialog } from "../create";
import pluralize from "pluralize";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";

interface CollectionsMenuBarProps {
  list?: string | undefined;
  show: boolean;
  query: string | null;
  // eslint-disable-next-line no-unused-vars
  handleSearchChange: (query: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  handleEditModeChange: (editMode: "create" | "rename" | "clone" | "delete" | null) => void;
}

export const CollectionsMenuBar: React.FC<CollectionsMenuBarProps> = ({
  show,
  query,
  handleSearchChange,
  handleEditModeChange,
}) => {
  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

  const seekEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.seekEnabled", false),
  );

  const { 
    trackRenameCollectionClicked,
    trackDeleteCollectionClicked,
    trackCloneCollectionClicked
  } = useCollectionsAdoption();

  useEffect(() => {
    api.start();
  }, [show]);

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.collectionsMenuBar)}>
          <Actions.Container>
            <Actions.Row>
              {userRoleService.canAccessSearch() && (
                <Actions.Search query={query} onSearch={handleSearchChange} />
              )}
              {seekEnabled && (
                <Actions.Seek />
              )}
            </Actions.Row>
            <Actions.Row>
              <Actions.IconButton
                icon="paperflite-rename"
                tooltip="Rename"
                onClick={() => {
                  handleEditModeChange("rename");
                  trackRenameCollectionClicked();
                }}
              />
              <Actions.IconButton
                icon="paperflite-delete"
                tooltip="Delete"
                onClick={() => {
                  handleEditModeChange("delete");
                  trackDeleteCollectionClicked();
                }}
              />
              <Actions.IconButton
                icon="paperflite-copy"
                tooltip="Clone"
                onClick={() => {
                  trackCloneCollectionClicked();
                  handleEditModeChange("clone")
                }}
              />

              {userRoleService.canCreateCollection() && (
                <Actions.Button
                  width={4}
                  icon={"paperflite-add"}
                  title="Create new collection"
                  onClick={() => handleEditModeChange("create")}
                />
              )}
            </Actions.Row>
          </Actions.Container>
        </animated.div>
      ),
  );
};

interface RenameMenubarProps {
  show: boolean;
  list?: string | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RenameMenubar: React.FC<RenameMenubarProps> = ({
  show,
  list,
  onConfirm,
  onCancel,
}) => {

  const { trackCollectionRenamed } = useCollectionsAdoption();
  const { edits } = React.useContext(CollectionsContext);

  const renaming = useSelector((state: any) => {
    const collectionUpdates = _.keys(state.collections.update).filter((key) => {
      const item = state.collections.update[key];
      return item.updating;
    });

    const listUpdates = _.keys(state.collections.lists.update).filter((key) => {
      const item = state.collections.lists.update[key];
      return item.updating;
    });

    return _.size(collectionUpdates) > 0 || _.size(listUpdates) > 0;
  });

  const renameRequest = useSelector((state: any) => {
    let collections = _.get(state, `collections.items`);
    if(list) {
      collections = _.get(state, `collections.filtered['${list}'].items`);
    }

    const requests = edits.filter((edit) => {
      return edit.type === "collection" && edit.value !== null;
    }).map((edit) => {
      const collection = collections?.find((c: Collection) => c.id === edit.id); 
      if(!collection) {
        return null;
      }

      const oldName = collection.name;
      const newName = edit.value;

      if(oldName === newName) {
        return null;
      }

      return {
        id: edit.id,
        oldName: collection.name,
        newName: edit.value as string,
      };
    }).filter((r) => r !== null);

    return requests;
  }, _.isEqual);

  useEffect(() => {
    if (!renaming) {
      onCancel();
    }
  }, [renaming]);

  const handleConfirm = () => {
    renameRequest.forEach((request) => {
      trackCollectionRenamed({
        oldName: request!.oldName,
        newName: request!.newName,
      });
    });

    onConfirm();
  }

  return (
    <Reveal show={show} className={styles.renameConfirmation}>
      <div className={styles.message}>
        Rename the collections and click here to
      </div>
      <Button
        variant="primary"
        icon="paperflite-tick"
        disabled={renaming}
        text={renaming ? "Saving..." : "Save"}
        onClick={handleConfirm}
      />
      <Button variant="secondary" text="Cancel" disabled={renaming} onClick={onCancel} />
    </Reveal>
  );
};

interface CloneMenubarProps {
  show: boolean;
  onCancel: () => void;
}

export const CloneMenubar: React.FC<CloneMenubarProps> = ({
  show,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { editMode, edits, onEditChanges } = React.useContext(CollectionsContext);
  const [cloneCollection, setCloneCollection] = React.useState<Collection | null>(null);

  useEffect(() => {
    if (edits && edits.length > 0 && editMode === "clone") {
      const collections = _.filter(edits, (edit) => {
        return edit.type === "collection" && edit.value !== null;
      });

      if (collections.length > 0) {
        setCloneCollection(collections[0].value as Collection);
      }
    }
  }, [edits]);

  const handleCloneCancel = () => {
    if (onEditChanges && cloneCollection && cloneCollection.id) {
      onEditChanges({
        id: cloneCollection.id,
        type: "collection",
        value: null,
      });
    }

    setCloneCollection(null);
    dispatch(invalidateCollectionCreationState());
  };

  const handleCloneFinish = (collection: Collection) => {
    handleCloneCancel();
    navigate(`/collections/${collection.id}`);
  };

  return (
    <>
      <Reveal show={show} className={styles.cloneConfirmation}>
        <div className={styles.message}>
          Click on the collection you wish to clone.
        </div>
        <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>
      </Reveal>
      {cloneCollection && (
        <CloneConfirmationDialog
          show={!!cloneCollection}
          collection={cloneCollection as Collection}
          onFinish={handleCloneFinish}
          onClose={handleCloneCancel}
        />
      )}
    </>
  );
};

interface CloneConfirmationDialogProps {
  show: boolean;
  collection: Collection;
  // eslint-disable-next-line no-unused-vars
  onFinish: (collection: Collection) => void;
  onClose: () => void;
}

export const CloneConfirmationDialog: React.FC<
  CloneConfirmationDialogProps
> = ({ show, collection, onFinish, onClose }) => {
  const dispatch = useDispatch();
  const { trackCloneCollectionConfirmed, trackCollectionCloned, trackCloneCollectionConfirmationScreenView } = useCollectionsAdoption();

  const cloning = useSelector(
    (state) => _.get(state, `collections.creation.creating`) || false,
  );

  const clonedCollection = useSelector((state) => {
    const finished = _.get(state, `collections.creation.created`) || false;
    if (finished) {
      return _.get(state, `collections.creation.item`);
    }

    return null;
  });

  const [name, setName] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if(show) {
      trackCloneCollectionConfirmationScreenView();
    }
  }, [show]);
  
  useEffect(() => {
    if (clonedCollection) {
      const clonedName = _.get(clonedCollection, "name");
      trackCollectionCloned({
        sourceName: collection.name!,
        clonedName: clonedName,
      });
      
      onFinish(clonedCollection);
    }
  }, [clonedCollection]);

  const validateForm = () => {
    if (_.isEmpty(name)) {
      return "Please provide a valid name";
    }

    if (_.size(name) > 200) {
      return "Folder name cannot be greater than 200 characters";
    }

    return null;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  };

  const handleConfirm = () => {
    trackCloneCollectionConfirmed(collection.name!);

    const e = validateForm();
    if (e) {
      setError(e);
      return;
    }

    const request = {
      name: name,
      collectionId: collection.id,
    };

    dispatch(createCollection(request));
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConfirm();
  };

  return (
    <Slider.Dialog
      className={styles.cloneConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header title="cloning..." message="1 collection"></Slider.Header>
      <Slider.Body>
        <div className={styles.details}>
          <div className={styles.collection}>
            <CollectionCard item={collection} readonly />
          </div>

          <form className={styles.addListForm} onSubmit={handleSubmit}>
            <Input
              autoFocus
              className={styles.input}
              placeholder="Add a collection name..."
              value={name}
              onChange={handleNameChange}
            />
            <Reveal show={!!error}>
              <div className={styles.error}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                {error}
              </div>
            </Reveal>
          </form>
          <p className={styles.description}>
            Clone away! Create an exact copy of this collection to tweak and
            share.. Just don&apos;t forget to personalize it—no one likes a copycat!
          </p>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={cloning ? "Cloning..." : "Confirm"}
          variant="primary"
          disabled={cloning}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={cloning}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface CloneHelpToolbarProps {
  show: boolean;
}

export const CloneHelpToolbar: React.FC<CloneHelpToolbarProps> = ({show}) => {
  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span className={styles.cloneCollectionHelper}>Pick a collection</span>
        </Toolbar.Text>
      </Toolbar.Container>
    </>
  );
};

//Delete Collections
interface DeleteMenubarProps {
  show: boolean;
  onCancel: () => void;
}

export const DeleteMenubar: React.FC<DeleteMenubarProps> = ({
  show,
  onCancel,
}) => {
  const deleting = false;
  const [confirm, setConfirm] = React.useState(false);

  const { edits } = React.useContext(CollectionsContext);
  const collections = edits
    .filter((edit) => edit.value)
    .map((edit) => edit.value as Collection);

  const onConfirm = () => {
    if (_.size(collections) == 0) {
      return;
    }

    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onCancel();
  };

  return (
    <Reveal show={show} className={styles.deleteConfirmation}>
      <div className={styles.message}>
        Click on one or more collection you wish to delete.
      </div>
      <Button
        variant="primary"
        icon="paperflite-delete"
        text={deleting ? "Deleting..." : "Delete"}
        disabled={_.size(collections) == 0}
        onClick={onConfirm}
      />
      <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>

      <DeleteConfirmationDialog
        show={confirm}
        collections={collections}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </Reveal>
  );
};

interface DeleteSelectionToolbarProps {
  show: boolean;
  collections: Collection[];
  onFinish: () => void;
}

export const DeleteSelectionToolbar: React.FC<DeleteSelectionToolbarProps> = ({
  show,
  collections,
  onFinish,
}) => {
  const [confirm, setConfirm] = React.useState(false);

  const handleShowConfirm = () => {
    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onFinish();
  };

  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span>{_.size(collections)} Selected</span>
        </Toolbar.Text>
        <Toolbar.Divider />
        <Toolbar.IconButton
          icon="paperflite-delete"
          disabled={_.size(collections) == 0}
          onClick={handleShowConfirm}
        />
      </Toolbar.Container>
      <DeleteConfirmationDialog
        show={confirm}
        collections={collections}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </>
  );
};

interface DeleteConfirmationDialogProps {
  show: boolean;
  collections: Collection[];
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteConfirmationDialog: React.FC<
  DeleteConfirmationDialogProps
> = ({ show, collections, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const { trackCollectionDeleted } = useCollectionsAdoption();

  const deleting = useSelector((state: any) => {
    const collectionDeletions = _.keys(state.collections.deletion).filter(
      (key) => {
        const item = state.collections.deletion[key];
        return item.deleting;
      },
    );

    return _.size(collectionDeletions) > 0;
  });

  const deleted = useSelector((state: any) => {
    const collectionDeletions = _.keys(state.collections.deletion).filter(
      (key) => {
        const item = state.collections.deletion[key];
        return item.deleted;
      },
    );

    return _.size(collectionDeletions) > 0;
  });

  const handleConfirm = () => {
    if (_.size(collections) == 0) {
      return;
    }

    collections.forEach((collection) => {
      if (collection.id) {
        dispatch(
          deleteCollection({
            id: collection.id,
          }),
        );

        trackCollectionDeleted(collection.name!);
      }
    });
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!deleting && deleted) {
      onFinish();
    }
  }, [deleting, deleted]);

  return (
    <Slider.Dialog
      className={styles.deleteConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        title="deleting..."
        message={`${_.size(collections)} ${pluralize("collection", _.size(collections))}`}
      >
        <p className={styles.description} style={{ marginBottom: 10 }}>
          Are You Sure? Are you really ready to say goodbye to these
          collections? No takebacksies!
        </p>
        <p className={styles.description} style={{ marginTop: 0, marginBottom: 10 }}>
          <b>Note:</b> Collections will still be accessible by external recipients.
        </p>
        <p className={styles.description} style={{ marginTop: 0, marginBottom: 10 }}>
          Edit your deleted collections from Conversations &lt; Edit Collection.
        </p>
      </Slider.Header>
      <Slider.Body>
        <div className={styles.grid}>
          {collections.map((collection, i) => {
            return <CollectionCard key={i} item={collection} readonly />;
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          variant="primary"
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};
